import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared";
import { StudentsApi, type SearchQueryRequest } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUseStudentsGetProps {
  page?: number;
  pageSize?: number;
  lastSelectedId?: number | null;
  active?: boolean;
  enabled?: boolean;
  /**
   * When set (non-empty criteria), calls `POST /api/students/search` instead of scroll.
   * Field names must be PascalCase EF properties (FirstName, LastName, Location.Name, etc.).
   * Each criterion can carry `logicalOperator` (OR / AND) to combine with the previous one.
   */
  searchQueryRequest?: SearchQueryRequest | null;
}

export const useStudentsGet = ({
  page = 1,
  pageSize = 1000,
  lastSelectedId,
  active,
  enabled = true,
  searchQueryRequest,
}: IUseStudentsGetProps = {}) => {
  const accessToken = useAccessToken();
  const useSearch =
    searchQueryRequest != null &&
    Array.isArray(searchQueryRequest.criteria) &&
    searchQueryRequest.criteria.length > 0;

  const { data, isSuccess, isError, isPending, isFetching, error, refetch } =
    useQuery({
    queryKey: [
      QueryKeys.Students,
      useSearch ? "search" : "scroll",
      page,
      pageSize,
      lastSelectedId,
      active,
      searchQueryRequest,
    ],
    queryFn: async () => {
      const service = createAuthenticatedApi(StudentsApi, accessToken!);
      const pageArg = page ?? 1;
      const scrollParams = {
        page: pageArg,
        pageSize,
        lastSelectedId: lastSelectedId ?? undefined,
        active,
      };

      if (useSearch) {
        return service.apiStudentsSearchPost({
          ...scrollParams,
          searchQueryRequest,
        });
      }

      return service.apiStudentsScrollGet(scrollParams);
    },
    enabled: !!accessToken && enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    throwOnError: false,
  });

  return { data, isSuccess, isError, isPending, isFetching, error, refetch };
};
